// src/pages/Properties/components/PropertyPhotoGallery.tsx
import {useState, useCallback} from "react";
import {
  Upload,
  Star,
  Trash2,
  ArrowUp,
  ArrowDown,
  Grid,
  List,
  Plus,
  Image as ImageIcon,
} from "lucide-react";
import {Button, Badge} from "../../../components/ui";
import {useToast} from "../../../hooks/common/useToast";
import {useConfirm} from "../../../hooks/common/useConfirm";
import {clsx} from "clsx";
import type {PropertyResponseDto} from "../../../types/property";
import {
  useGetPropertyPhotosQuery,
  useUploadPropertyPhotosMutation,
  useDeletePropertyPhotoMutation,
  useSetPrimaryPhotoMutation,
  useReorderPropertyPhotosMutation,
} from "../../../store/api/propertyPhotoApi";

interface PropertyPhotoGalleryProps {
  property: PropertyResponseDto;
  onClose?: () => void;
}

const PropertyPhotoGallery: React.FC<PropertyPhotoGalleryProps> = ({
  property,
  onClose,
}) => {
  const {success, error} = useToast();
  const {confirm} = useConfirm();
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  // ✅ RTK Query hooks - tags က automatic cache invalidation လုပ်ပေးမယ်
  const {
    data: currentPhotos = [],
    isLoading: photosLoading,
    isError: photosError,
  } = useGetPropertyPhotosQuery({propertyId: property.id});

  const [uploadPhotos, {isLoading: isUploading}] =
    useUploadPropertyPhotosMutation();
  const [deletePhoto, {isLoading: isDeleting}] =
    useDeletePropertyPhotoMutation();
  const [setPrimaryPhoto, {isLoading: isSettingPrimary}] =
    useSetPrimaryPhotoMutation();
  const [reorderPhotos, {isLoading: isReordering}] =
    useReorderPropertyPhotosMutation();

  // ✅ Use current photos from RTK Query, fallback to property.photos
  const photosToDisplay =
    Array.isArray(currentPhotos) && currentPhotos.length > 0
      ? currentPhotos
      : property.photos || [];

  // ✅ Upload with RTK Query mutation
  const handleFilesChange = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;

      // Validate files
      const validFiles: File[] = [];
      const maxSize = 5 * 1024 * 1024; // 5MB
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
      ];

      const filesArray = Array.from(files);

      for (const file of filesArray) {
        if (!file) continue;

        // Check file type
        if (!allowedTypes.includes(file.type)) {
          error(
            "Invalid File",
            `File ${file.name} is not a supported image format`
          );
          continue;
        }

        // Check file size
        if (file.size > maxSize) {
          error(
            "File Too Large",
            `File ${file.name} is too large. Maximum size is 5MB`
          );
          continue;
        }

        validFiles.push(file);
      }

      if (validFiles.length === 0) {
        error("No Valid Files", "Please select valid image files");
        return;
      }

      try {
        // ✅ RTK Query mutation - tags က automatic invalidation လုပ်ပေးမယ်
        await uploadPhotos({
          propertyId: property.id,
          files: validFiles,
        }).unwrap();

        success(
          "Success",
          `${validFiles.length} photo(s) uploaded successfully`
        );

        // ✅ Reset file input
        const fileInputs = document.querySelectorAll('input[type="file"]');
        fileInputs.forEach((input) => ((input as HTMLInputElement).value = ""));
      } catch (err: any) {
        console.error("❌ Upload error:", err);
        error(
          "Upload Failed",
          err?.data?.message || "Failed to upload photos. Please try again."
        );
      }
    },
    [property.id, uploadPhotos, success, error]
  );

  // ✅ Delete with RTK Query mutation
  const handleDelete = useCallback(
    async (photoId: number) => {
      const confirmed = await confirm({
        title: "Delete Photo",
        message:
          "Are you sure you want to delete this photo? This action cannot be undone.",
        variant: "danger",
        confirmText: "Delete",
      });

      if (!confirmed) return;

      try {
        // ✅ RTK Query mutation - automatic cache invalidation
        await deletePhoto(photoId).unwrap();
        success("Success", "Photo deleted successfully");
      } catch (err: any) {
        console.error("❌ Delete error:", err);
        error(
          "Delete Failed",
          err?.data?.message || "Failed to delete photo. Please try again."
        );
      }
    },
    [deletePhoto, confirm, success, error]
  );

  // ✅ Set primary with RTK Query mutation
  const handleSetPrimary = useCallback(
    async (photoId: number) => {
      try {
        // ✅ RTK Query mutation - automatic cache invalidation
        await setPrimaryPhoto({photoId}).unwrap();
        success("Success", "Primary photo updated successfully");
      } catch (err: any) {
        console.error("❌ Set primary error:", err);
        error(
          "Update Failed",
          err?.data?.message || "Failed to set primary photo. Please try again."
        );
      }
    },
    [setPrimaryPhoto, success, error]
  );

  // ✅ Reorder with RTK Query mutation
  // const handleMovePhoto = useCallback(
  //   async (photoId: number, direction: "up" | "down") => {
  //     const photos = [...photosToDisplay].sort(
  //       (a, b) => a.sortOrder - b.sortOrder
  //     );
  //     const currentIndex = photos.findIndex((p) => p.id === photoId);

  //     if (currentIndex === -1) return;

  //     const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;

  //     if (newIndex < 0 || newIndex >= photos.length) return;

  //     // Swap positions
  //     const newPhotos = [...photos];
  //     if (
  //       newPhotos[currentIndex] !== undefined &&
  //       newPhotos[newIndex] !== undefined
  //     ) {
  //       [newPhotos[currentIndex], newPhotos[newIndex]] = [
  //         newPhotos[newIndex],
  //         newPhotos[currentIndex],
  //       ];
  //     }

  //     // Update sort orders
  //     const photoIds = newPhotos.map((p) => p.id);

  //     try {
  //       // ✅ RTK Query mutation - automatic cache invalidation
  //       await reorderPhotos({
  //         propertyId: property.id,
  //         data: {photoIds},
  //       }).unwrap();

  //       success("Success", "Photos reordered successfully");
  //     } catch (err: any) {
  //       console.error("❌ Reorder error:", err);
  //       error(
  //         "Reorder Failed",
  //         err?.data?.message || "Failed to reorder photos. Please try again."
  //       );
  //     }
  //   },
  //   [photosToDisplay, property.id, reorderPhotos, success, error]
  // );

  // ✅ Sort photos properly
  const sortedPhotos = [...photosToDisplay].sort((a, b) => {
    // Primary photo first, then by sort order
    if (a.isPrimary && !b.isPrimary) return -1;
    if (!a.isPrimary && b.isPrimary) return 1;
    return a.sortOrder - b.sortOrder;
  });

  // Loading state
  if (photosLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            Loading photos...
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (photosError) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500">Failed to load photos. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Property Photos ({sortedPhotos.length})
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Manage photos for {property.titleEng}
          </p>
        </div>

        <div className="flex items-center space-x-2">
          {/* View Mode Toggle */}
          <div className="flex bg-gray-100 dark:bg-dark-700 rounded-lg p-1">
            <button
              onClick={() => setViewMode("grid")}
              className={clsx(
                "p-2 rounded-md transition-colors",
                viewMode === "grid"
                  ? "bg-white dark:bg-dark-600 text-gray-900 dark:text-white shadow-sm"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              )}
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={clsx(
                "p-2 rounded-md transition-colors",
                viewMode === "list"
                  ? "bg-white dark:bg-dark-600 text-gray-900 dark:text-white shadow-sm"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              )}
            >
              <List className="w-4 h-4" />
            </button>
          </div>

          {/* Upload Button */}
          <div className="relative">
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => handleFilesChange(e.target.files)}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              disabled={isUploading}
              id="photo-upload"
            />
            <Button
              variant="primary"
              size="sm"
              disabled={isUploading}
              className="pointer-events-none"
            >
              <Plus className="w-4 h-4 mr-2" />
              {isUploading ? "Uploading..." : "Add Photos"}
            </Button>
          </div>
        </div>
      </div>

      {/* Photos Grid/List */}
      {sortedPhotos.length === 0 ? (
        <div className="text-center py-12">
          <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
            No photos yet
          </h4>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Upload some photos to get started
          </p>
        </div>
      ) : viewMode === "grid" ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {sortedPhotos.map((photo, index) => (
            <div
              key={photo.id}
              className="relative group bg-white dark:bg-dark-800 rounded-lg overflow-hidden shadow-sm border border-gray-200 dark:border-dark-700"
            >
              {/* Photo */}
              <div className="aspect-square">
                <img
                  src={photo.photoUrl}
                  alt={`Photo ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Primary Badge */}
              {photo.isPrimary && (
                <div className="absolute top-2 left-2">
                  <Badge variant="warning" size="sm">
                    <Star className="w-3 h-3 mr-1" />
                    Primary
                  </Badge>
                </div>
              )}

              {/* Actions */}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                <div className="flex space-x-2">
                  {!photo.isPrimary && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleSetPrimary(photo.id)}
                      disabled={isSettingPrimary}
                      className="bg-white/20 hover:bg-white/30 text-white"
                    >
                      <Star className="w-4 h-4" />
                    </Button>
                  )}

                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDelete(photo.id)}
                    disabled={isDeleting}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {sortedPhotos.map((photo, index) => (
            <div
              key={photo.id}
              className="flex items-center space-x-4 p-4 bg-white dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-dark-700"
            >
              {/* Thumbnail */}
              <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                <img
                  src={photo.photoUrl}
                  alt={`Photo ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Info */}
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    Photo {index + 1}
                  </span>
                  {photo.isPrimary && (
                    <Badge variant="warning" size="sm">
                      <Star className="w-3 h-3 mr-1" />
                      Primary
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Sort order: {photo.sortOrder}
                </p>
              </div>

              {/* Actions */}
              <div className="flex items-center space-x-2">
                {!photo.isPrimary && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSetPrimary(photo.id)}
                    disabled={isSettingPrimary}
                  >
                    Set Primary
                  </Button>
                )}

                {/* <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleMovePhoto(photo.id, "up")}
                  disabled={index === 0 || isReordering}
                >
                  <ArrowUp className="w-4 h-4" />
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleMovePhoto(photo.id, "down")}
                  disabled={index === sortedPhotos.length - 1 || isReordering}
                >
                  <ArrowDown className="w-4 h-4" />
                </Button> */}

                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleDelete(photo.id)}
                  disabled={isDeleting}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PropertyPhotoGallery;
